import { useCallback } from "react"
import { useMutation } from "urql"

import "@/screens/Accounting/subfeatures/PayableApprovals/graphql/changeInvoiceStatus.mutation"

import { type MutationLockResult, MutationLockResultStatus } from "@/entityLock/types"
import { toEntityLock } from "@/entityLock/utils"
import type {
  ChangeInvoiceStatusMutation,
  ChangeInvoiceStatusMutationVariables,
  InvoiceStatusChangeFailure,
  InvoiceStatusChangeInput,
} from "@/graphql/codegen/graphql"
import { ChangeInvoiceStatusDocument, InvoiceStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export type CancelInvoiceInput = Omit<InvoiceStatusChangeInput, "action" | "comment"> & {
  comment: string
}

export interface UseCancelInvoiceReturn {
  cancelInvoice: (input: CancelInvoiceInput) => Promise<MutationLockResult>
  isCancelling: boolean
}

export const useCancelInvoice = (): UseCancelInvoiceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    ChangeInvoiceStatusMutation,
    ChangeInvoiceStatusMutationVariables
  >(ChangeInvoiceStatusDocument)

  const cancelInvoice = useCallback(
    async (input: CancelInvoiceInput): Promise<MutationLockResult> => {
      const { data, error } = await executeMutation({
        input: {
          invoiceId: input.invoiceId,
          action: InvoiceStatus.Cancelled,
          comment: input.comment,
        },
      })
      const output = data?.invoice.statusChange

      if (output?.__typename === "EntityLockFailure") {
        const currentLock = toEntityLock(output.error.currentLock)!
        return {
          status: MutationLockResultStatus.LockConflict,
          currentLock,
          message: output.error.message,
        }
      }

      const hasError = error || output?.__typename === "InvoiceStatusChangeFailure"

      if (hasError) {
        const message = error?.message ?? (output as InvoiceStatusChangeFailure).error.message
        notify({ type: NotificationType.Error, message })
        return { status: MutationLockResultStatus.Error }
      }

      notify({ type: NotificationType.Success, message: "Invoice cancelled" })
      return { status: MutationLockResultStatus.Success }
    },
    [executeMutation, notify]
  )

  return { cancelInvoice, isCancelling: fetching }
}
